import type { ReactNode } from 'react';
import { Image, Pressable, StyleSheet, View, type GestureResponderEvent, type StyleProp, type ViewStyle } from 'react-native';
import { Text } from '@/components/common/AppText';
import { colors } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { SHORTLIST_STYLE_SCALE } from './shortlists-style-scale';

const LOGO_COMPACT = require('../../../assets/branding/logo-compact.png');
const LOGO_MAIN = require('../../../assets/branding/logo-main.png');
const DEFAULT_USER_AVATAR = require('../../../assets/default-avatar.png');

// ─── ShortlistAvatar ───────────────────────────────────────────────

interface ShortlistAvatarProps {
  initials?: string;
  tone?: string;
  size?: number;
  textSize?: number;
  style?: StyleProp<ViewStyle>;
  withBorder?: boolean;
  borderColor?: string;
  source?: any;
  imageUrl?: string | null;
}

export function ShortlistAvatar({
  size = SHORTLIST_STYLE_SCALE.controls.avatarSize,
  style,
  withBorder = false,
  borderColor = '#FFFFFF',
  source,
  imageUrl,
}: ShortlistAvatarProps) {
  const resolvedSource = imageUrl ? { uri: imageUrl } : (source ?? DEFAULT_USER_AVATAR);

  return (
    <View
      style={[
        styles.avatar,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: 'transparent',
          borderWidth: withBorder ? 2 : 0,
          borderColor: withBorder ? borderColor : 'transparent',
        },
        style,
      ]}
    >
      <Image
        source={resolvedSource}
        style={{ width: size, height: size, borderRadius: size / 2 }}
        resizeMode="cover"
      />
    </View>
  );
}

// ─── ShortlistIconButton ───────────────────────────────────────────

interface ShortlistIconButtonProps {
  children: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  style?: StyleProp<ViewStyle>;
  size?: number;
  tone?: 'surface' | 'primary';
}

export function ShortlistIconButton({
  children,
  onPress,
  style,
  size = SHORTLIST_STYLE_SCALE.controls.iconButtonSize,
  tone = 'surface',
}: ShortlistIconButtonProps) {
  const { theme } = useTheme();

  const surfaceStyle =
    tone === 'primary'
      ? {
          backgroundColor: colors.primary,
          borderColor: colors.primary,
        }
      : {
          backgroundColor: theme.surface,
          borderColor: theme.border,
        };

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.iconButton,
        {
          width: size,
          height: size,
          borderRadius: Math.round(size / 2),
        },
        surfaceStyle,
        style,
        pressed && { opacity: 0.88 },
      ]}
      hitSlop={8}
    >
      {children}
    </Pressable>
  );
}

// ─── ShortlistBrandMark ────────────────────────────────────────────

interface ShortlistBrandMarkProps {
  variant?: 'icon' | 'wordmark' | 'iconWithLabel';
  iconSize?: number;
  wordmarkWidth?: number;
  label?: string;
  style?: StyleProp<ViewStyle>;
  iconBackgroundColor?: string;
}

export function ShortlistBrandMark({
  variant = 'icon',
  iconSize = 48,
  wordmarkWidth = 124,
  label = 'Tabbio',
  style,
  iconBackgroundColor = colors.primary,
}: ShortlistBrandMarkProps) {
  const { theme } = useTheme();
  const { fonts } = useFont();

  if (variant === 'wordmark') {
    return (
      <View style={style}>
        <Image
          source={LOGO_MAIN}
          style={{ width: wordmarkWidth, height: Math.round(wordmarkWidth * 0.29) }}
          resizeMode="contain"
        />
      </View>
    );
  }

  if (variant === 'iconWithLabel') {
    return (
      <View style={[styles.brandStack, style]}>
        <View
          style={[
            styles.brandIconWrap,
            {
              width: iconSize,
              height: iconSize,
              borderRadius: Math.round(iconSize * 0.28),
              backgroundColor: iconBackgroundColor,
            },
          ]}
        >
          <Image
            source={LOGO_COMPACT}
            style={{ width: Math.round(iconSize * 0.58), height: Math.round(iconSize * 0.58) }}
            resizeMode="contain"
          />
        </View>
        <Text style={[styles.brandLabel, { color: theme.textTertiary, fontFamily: fonts.regular }]}>
          {label}
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.brandIconWrap,
        {
          width: iconSize,
          height: iconSize,
          borderRadius: Math.round(iconSize * 0.28),
          backgroundColor: iconBackgroundColor,
        },
        style,
      ]}
    >
      <Image
        source={LOGO_COMPACT}
        style={{ width: Math.round(iconSize * 0.58), height: Math.round(iconSize * 0.58) }}
        resizeMode="contain"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  avatar: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  iconButton: {
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandStack: {
    alignItems: 'center',
    gap: 4,
  },
  brandIconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandLabel: {
    fontSize: 14,
  },
});
